import { useEffect, useRef } from 'react';

type PushToTalkOptions = {
  enabled: boolean;
  /** Режим рации включён: только тогда пробел вообще что-то значит. */
  pushToTalk: boolean;
  micEnabled: boolean;
  setMicEnabled: (enabled: boolean) => void;
};

const isTypingTarget = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  if (!element) {
    return false;
  }
  const tag = element.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable;
};

/**
 * M — переключить микрофон, всегда. Пробел — говорить, пока держишь, но **только в режиме рации**.
 *
 * <p>Пробел перехватывался всегда, вместе с `preventDefault`, и это было двойной ошибкой. Во-первых,
 * микрофон в разговоре открыт по умолчанию — удержание возвращало его в то же состояние, то есть
 * не делало ничего, а подсказка в окне называла этот приём главным способом говорить. Во-вторых,
 * пока человек сидел в голосе, он не мог пролистать страницу пробелом.
 *
 * <p>Удержание возвращает микрофон в исходное состояние, а не включает насовсем: иначе однократное
 * нажатие незаметно оставило бы человека с открытым микрофоном.
 *
 * <p>Только клавиатура, только десктоп: на тач-устройствах этих клавиш нет, а перехватывать пробел
 * там, где человек печатает, нельзя — поэтому поля ввода и contenteditable исключены.
 */
export const usePushToTalk = ({
  enabled,
  pushToTalk,
  micEnabled,
  setMicEnabled,
}: PushToTalkOptions) => {
  const heldRef = useRef(false);
  const micBeforeHoldRef = useRef(micEnabled);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target) || event.repeat) {
        return;
      }
      if (event.code === 'Space' && pushToTalk) {
        event.preventDefault();
        heldRef.current = true;
        micBeforeHoldRef.current = micEnabled;
        setMicEnabled(true);
      }
      if (event.code === 'KeyM') {
        setMicEnabled(!micEnabled);
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space' && heldRef.current) {
        heldRef.current = false;
        setMicEnabled(micBeforeHoldRef.current);
      }
    };

    // Окно потеряло фокус с зажатым пробелом — иначе микрофон останется открытым навсегда.
    const onBlur = () => {
      if (heldRef.current) {
        heldRef.current = false;
        setMicEnabled(micBeforeHoldRef.current);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [enabled, pushToTalk, micEnabled, setMicEnabled]);
};
